import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { LogIn, UserPlus, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { startGoogleSignIn } from '@/lib/googleOAuth';
import { ATLAS_LOGO_URL, ATLAS_NAME } from '@/lib/brand';

// Helper to read the persisted "last login" from localStorage. Returns null if absent/corrupt.
const readLastLogin = () => {
  try {
    const raw = localStorage.getItem('last_login');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.email) return null;
    return parsed;
  } catch { return null; }
};

// Initials helper for the avatar fallback
const initials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export default function StudentLogin() {
  const navigate = useNavigate();
  const { login, register, user, forgetLastLogin } = useAuth();
  const [tab, setTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [lastLogin, setLastLogin] = useState(() => readLastLogin());
  const passwordInputRef = useRef(null);

  const [loginForm, setLoginForm] = useState({
    email: lastLogin?.method === 'password' ? lastLogin.email : '',
    password: '',
  });
  const [regForm, setRegForm] = useState({
    name: '', email: '', password: '', role: 'teacher'
  });

  // The Google OAuth callback is now handled by the dedicated /auth/google/callback
  // page (see AuthCallbackPage.js); no in-page hash handling here.

  // Redirect if already logged in
  useEffect(() => {
    if (user && user.status === 'approved') {
      navigate('/');
    }
  }, [user, navigate]);

  const handleGoogleClick = async () => {
    try {
      setGoogleLoading(true);
      await startGoogleSignIn();  // navigates away — never returns
    } catch (err) {
      toast.error(err?.message || 'Could not start Google sign-in');
      setGoogleLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(loginForm.email, loginForm.password);
      if (u.status === 'pending') {
        toast.info('Your account is awaiting admin approval.');
        navigate('/');
      } else {
        toast.success(`Welcome back, ${u.name}!`);
        navigate('/');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (regForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const u = await register(
        regForm.name, regForm.email, regForm.password,
        regForm.role, null
      );
      if (u.status === 'pending') {
        toast.info('Account created! Awaiting admin approval.');
        navigate('/');
      } else {
        toast.success(`Welcome, ${u.name}!`);
        navigate('/');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (googleLoading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Card className="p-8 text-center max-w-sm border-[#EBE7E0]">
          <Loader2 className="h-8 w-8 animate-spin text-[#14213D] mx-auto mb-4" />
          <h2 className="font-serif-display text-lg text-[#14213D]">Signing you in…</h2>
          <p className="text-sm text-[#5A6577] mt-1">Verifying your account</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative bg-[#FDFBF7] flex items-center justify-center px-4 py-10 overflow-hidden">
      {/* Flint Hills sunset — visual signature shared with landing + About */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_e079eb76-7fdc-4c18-ab8a-6dab3e979ce5/artifacts/nxbal9sr_kansas-photography.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center 60%',
          opacity: 0.16,
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#FDFBF7]/70 via-[#FDFBF7]/50 to-[#FDFBF7]/85 pointer-events-none" aria-hidden="true" />

      <div className="w-full max-w-md relative">
        <div className="text-center mb-8">
          <button onClick={() => navigate('/')} className="inline-flex flex-col items-center gap-3 group" data-testid="login-brand">
            <div className="w-16 h-16 rounded-lg bg-white border border-[#EBE7E0] flex items-center justify-center overflow-hidden shadow-sm">
              <img src={ATLAS_LOGO_URL} alt="" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="font-serif-display text-2xl text-[#14213D] tracking-tight">{ATLAS_NAME}</h1>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#5A6577] mt-1">Editorial Archive</p>
            </div>
          </button>
          <p className="text-sm text-[#5A6577] mt-6">Sign in to access your curriculum.</p>
        </div>

        {/* "Continue as" — returning-user quick-resume */}
        {lastLogin && (
          <Card className="p-4 mb-4 border border-amber-200 bg-amber-50/40 relative" data-testid="continue-as-card">
            <button
              type="button"
              onClick={() => { forgetLastLogin(); setLastLogin(null); }}
              className="absolute top-2 right-2 text-stone-400 hover:text-stone-700 p-1 rounded"
              aria-label="Forget this account"
              data-testid="continue-as-dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <p className="text-xs font-medium text-stone-500 mb-2 uppercase tracking-wider">Welcome back</p>
            <button
              type="button"
              onClick={() => {
                if (lastLogin.method === 'google') {
                  handleGoogleClick();
                } else {
                  // Password method: pre-fill email and focus password field
                  setLoginForm(f => ({ ...f, email: lastLogin.email }));
                  setTab('login');
                  setTimeout(() => passwordInputRef.current?.focus(), 50);
                }
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white hover:bg-amber-50 border border-stone-200 transition-colors text-left"
              data-testid="continue-as-btn"
            >
              {lastLogin.picture ? (
                <img
                  src={lastLogin.picture}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover border border-stone-200"
                  referrerPolicy="no-referrer"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-amber-700 text-amber-50 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                  {initials(lastLogin.name || lastLogin.email)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-stone-900 truncate">
                  Continue as {lastLogin.name || lastLogin.email.split('@')[0]}
                </p>
                <p className="text-xs text-stone-500 truncate">{lastLogin.email}</p>
              </div>
              <span className="text-xs text-amber-700 font-medium whitespace-nowrap">
                {lastLogin.method === 'google' ? 'Google' : 'Password'}
              </span>
            </button>
          </Card>
        )}

        {/* Google Sign-In */}
        <Card className="p-5 mb-4 border border-stone-200" data-testid="google-signin-card">
          <p className="text-sm font-medium text-stone-700 mb-3 text-center">Quick Sign In</p>
          <button
            type="button"
            onClick={handleGoogleClick}
            disabled={googleLoading}
            className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-stone-300 rounded-lg bg-white hover:bg-stone-50 transition-colors text-sm font-medium text-stone-700 disabled:opacity-60"
            data-testid="google-signin-btn"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
              <path d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Sign in with Google
          </button>
          <p className="text-xs text-stone-400 text-center mt-2">Any Google account</p>
        </Card>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-stone-300" />
          <span className="text-xs text-stone-400 uppercase tracking-wider">or</span>
          <div className="flex-1 h-px bg-stone-300" />
        </div>

        {/* Email/Password for Admin/Teacher */}
        <Card className="p-6" data-testid="email-login-card">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="text-sm text-stone-600 mb-1 block">Email</label>
                  <Input
                    type="email" required
                    value={loginForm.email}
                    onChange={e => setLoginForm({...loginForm, email: e.target.value})}
                    placeholder="admin@usd492.org"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-stone-600 mb-1 block">Password</label>
                  <Input
                    ref={passwordInputRef}
                    type="password" required
                    value={loginForm.password}
                    onChange={e => setLoginForm({...loginForm, password: e.target.value})}
                    placeholder="Enter password"
                    data-testid="login-password-input"
                  />
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="login-submit-btn">
                  <LogIn className="h-4 w-4 mr-2" />
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
                <div className="text-center pt-1">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs text-[#5A6577] hover:text-[#14213D] underline-offset-2 hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="text-sm text-stone-600 mb-1 block">Full Name</label>
                  <Input
                    required
                    value={regForm.name}
                    onChange={e => setRegForm({...regForm, name: e.target.value})}
                    placeholder="Your name"
                    data-testid="register-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-stone-600 mb-1 block">Email</label>
                  <Input
                    type="email" required
                    value={regForm.email}
                    onChange={e => setRegForm({...regForm, email: e.target.value})}
                    placeholder="your.email@usd492.org"
                    data-testid="register-email-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-stone-600 mb-1 block">Password</label>
                  <Input
                    type="password" required
                    value={regForm.password}
                    onChange={e => setRegForm({...regForm, password: e.target.value})}
                    placeholder="Min 6 characters"
                    data-testid="register-password-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-stone-600 mb-1 block">Role</label>
                  <select
                    className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm"
                    value={regForm.role}
                    onChange={e => setRegForm({...regForm, role: e.target.value})}
                    data-testid="register-role-select"
                  >
                    <option value="teacher">Teacher</option>
                  </select>
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="register-submit-btn">
                  <UserPlus className="h-4 w-4 mr-2" />
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}
